package org.catharsis.spendtel.feature.data

import kotlinx.serialization.Serializable

@Serializable
data class StoragePathInput(
    val storagePath: String,
    val fileName: String,
)

@Serializable
data class CreateExpenseReportRequest(
    val storagePaths: List<StoragePathInput>,
)

@Serializable
data class SourceDocument(
    val storagePath: String,
    val fileName: String? = null,
    val contentType: String? = null,
    val size: Long? = null,
)

@Serializable
data class ExpenseReportResponse(
    val reportId: String,
    val status: String,
    val sourceDocuments: List<SourceDocument>? = null,
    val error: String? = null,
    val summary: ReportSummary? = null,
    val monthlyStory: MonthlyStory? = null,
    val lifeCategories: List<LifeCategory>? = null,
    val financialPersonality: FinancialPersonality? = null,
    val merchantInsights: List<MerchantInsight>? = null,
    val spendingTimeline: List<TimelineEntry>? = null,
    val whatChanged: WhatChanged? = null,
    val aboutYou: List<String>? = null,
    val spendingStories: List<SpendingStory>? = null,
    val transactions: List<Transaction>? = null,
    val financialGoalUsed: String? = null,
    val equivalences: List<SpendingEquivalence>? = null,
    val goalInsights: List<GoalInsight>? = null,
    val spendingPatterns: SpendingPatterns? = null,
    val trends: ReportTrends? = null,
    val moneyLeaks: MoneyLeaks? = null,
    val highlights: List<String>? = null,
)

@Serializable
data class ReportSummary(
    val totalSpending: Double,
    val currency: String,
    val periodStart: String? = null,
    val periodEnd: String? = null,
)

@Serializable
data class MonthlyStory(
    val headline: String,
    val observations: List<String>,
)

@Serializable
data class LifeCategory(
    val name: String,
    val amount: Double,
    val insight: String,
)

@Serializable
data class FinancialPersonality(
    val label: String,
    val description: String,
)

@Serializable
data class MerchantInsight(
    val merchant: String,
    val visits: Int,
    val total: Double,
    val pattern: String,
    val averageSpend: Double? = null,
    val trend: String? = null,
)

@Serializable
data class TimelineEntry(
    val date: String,
    val narrative: String,
)

@Serializable
data class WhatChanged(
    val biggestIncreases: List<ChangeItem>,
    val biggestDecreases: List<ChangeItem>,
    val newHabits: List<String>,
)

@Serializable
data class ChangeItem(
    val label: String,
    val change: String,
)

@Serializable
data class SpendingStory(
    val title: String,
    val insights: List<String>,
)

@Serializable
data class Transaction(
    val date: String,
    val merchant: String,
    val amount: Double,
    val lifeCategory: String,
    val description: String? = null,
)

@Serializable
data class SpendingEquivalence(
    val sourceLabel: String,
    val amount: Double,
    val comparison: String,
    val insight: String,
)

@Serializable
data class GoalInsight(
    val goal: String,
    val insight: String,
    val suggestion: String,
)

@Serializable
data class MerchantShare(
    val merchant: String,
    val percent: Double,
    val total: Double,
)

@Serializable
data class SpendingPatterns(
    val weekdayVsWeekend: String? = null,
    val busiestSpendingDay: String? = null,
    val topMerchantsByShare: List<MerchantShare> = emptyList(),
    val convenienceShare: Double? = null,
    val diningShare: Double? = null,
)

@Serializable
data class ReportTrends(
    val spendingVsLastReport: String? = null,
    val personalityShift: String? = null,
    val recurringTheme: String? = null,
)

@Serializable
data class SubscriptionLeak(
    val name: String,
    val amount: Double,
    val frequency: String,
    val annualized: Double,
)

@Serializable
data class FeeLeak(
    val label: String,
    val amount: Double,
)

@Serializable
data class MoneyLeaks(
    val subscriptions: List<SubscriptionLeak> = emptyList(),
    val feesAndInterest: List<FeeLeak> = emptyList(),
    val totalMonthlySubscriptions: Double? = null,
    val totalFees: Double? = null,
    val summary: String? = null,
)

fun SourceDocument.displayName(): String {
    return fileName?.takeIf { it.isNotBlank() }
        ?: storagePath.substringAfterLast('/')
}
